import { useMemo } from 'react';
import { useQuery } from 'react-query';

import { useAppSelector } from '~/hooks/useAppSelector';
import { api } from '~/services/api';
import { IActivityCategoryDTO } from '~/types/dtos';
import { IEcosystem } from '~/types/interfaces/ecosystem';

const queryFilter = {
  causeOptions: {
    filter: JSON.stringify({ relatedTo: ['causeOptions'] }),
  },
  requirementOptions: {
    filter: JSON.stringify({ relatedTo: ['requirementOptions'] }),
  },
};

/**
 * @param canLoadCauses - Whether to load the causes options or not
 * @param canLoadRequirements - Whether to load the requirements options or not
 */
export const useActivityCategoryOptions = (
  canLoadCauses = true,
  canLoadRequirements = true,
  ecosystem?: IEcosystem,
) => {
  const { selectedEcosystem } = useAppSelector(({ ecosystem }) => ecosystem);

  const ecosystemId = ecosystem?._id || selectedEcosystem?._id;
  const causeOptionIds = useMemo(() => {
    return ecosystem?.causeOptions || selectedEcosystem?.causeOptions || [];
  }, [ecosystem?.causeOptions, selectedEcosystem?.causeOptions]);

  const { data: causeOptionsData, isFetching: isLoadingCauses } = useQuery({
    queryKey: ['causeOptions', causeOptionIds, ecosystemId],
    queryFn: async () => {
      const response = await api.get('activity-category', {
        //TODO: In the future if needed add pagination, for now we're not getting all the options without this
        params: {
          filter: JSON.stringify({
            relatedTo: ['causeOptions'],
            _id: { $in: causeOptionIds },
          }),
          limit: 200,
        },
      });

      return response.data;
    },
    enabled: canLoadCauses,
  });
  const { data: requirementOptionsData, isFetching: isLoadingRequirements } =
    useQuery({
      queryKey: 'requirementOptions',
      queryFn: async () => {
        const response = await api.get('activity-category', {
          //TODO: In the future if needed add pagination, for now we're not getting all the options without this
          params: { ...queryFilter.requirementOptions, limit: 200 },
        });

        return response.data;
      },
      enabled: canLoadRequirements,
    });

  const causeOptions: { value: string; label: string }[] = useMemo(() => {
    if (
      !isLoadingCauses &&
      causeOptionsData &&
      causeOptionsData?.data?.length > 0
    ) {
      const uniqueCauses = causeOptionsData.data.reduce(
        (
          acc: { value: string; label: string }[],
          current: IActivityCategoryDTO,
        ) => {
          const existsCause = acc.find(
            (accCause) => accCause.label === current.displayName,
          );

          if (!existsCause) {
            acc.push({
              value: current._id,
              label: current.displayName,
            });
          }
          return acc;
        },
        [] as { value: string; label: string }[],
      );
      return uniqueCauses;
    }
    return [];
  }, [causeOptionsData, isLoadingCauses]);

  const requirementOptions = useMemo(() => {
    if (
      !isLoadingRequirements &&
      requirementOptionsData &&
      requirementOptionsData?.data?.length > 0
    ) {
      const uniqueRequirements = requirementOptionsData.data.reduce(
        (
          acc: { value: string; label: string }[],
          current: IActivityCategoryDTO,
        ) => {
          const existsRequirement = acc.find(
            (accRequirement) => accRequirement.label === current.displayName,
          );

          if (!existsRequirement) {
            acc.push({
              value: current._id,
              label: current.displayName,
            });
          }
          return acc;
        },
        [] as { value: string; label: string }[],
      );
      return uniqueRequirements;
    }
    return [];
  }, [requirementOptionsData, isLoadingRequirements]);

  return {
    requirementOptions,
    causeOptions,
    requirementOptionsData,
    causeOptionsData,
  };
};
